import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import EmailAssociatedEntity from "@mapmycustomers/shared/types/email/EmailAssociatedEntity";
import {
  Company,
  EntityType,
  EntityTypesSupportingEmail,
} from "@mapmycustomers/shared/types/entity";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";

import LockedEntityType from "@app/component/activity/LockedFormFields/LockedEntityType";
import { getAssociationsState } from "@app/component/createEditEntity/Activity/store";
import {
  changeAssociatedEntities,
  initializeActivityModal,
  relateUnrelateEntities,
} from "@app/component/createEditEntity/Activity/store/actions";
import {
  CompanyRelationshipField,
  DealRelationshipField,
  PersonRelationshipField,
} from "@app/component/FormFields/components/Associations";
import { getEmailActivityType } from "@app/store/activity";
import { RootState } from "@app/store/rootReducer";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import useSchema from "@app/util/hook/useSchema";

import BaseEmailActivitiesCreateModal, {
  BaseEmailActivitiesCreateModalProps,
} from "../BaseEmailActivitiesCreateModal";
import EmailContext from "../EmailContext";

import messages from "./messages";

type RelationshipFieldAutoFillState = "partial" | boolean;

const getRelationshipFieldAutoFillState = (
  emailAssociatedEntities: EmailAssociatedEntity[],
  field: keyof EmailAssociatedEntity
): RelationshipFieldAutoFillState => {
  if (!emailAssociatedEntities.length) {
    return false;
  }

  const filledEntities = emailAssociatedEntities.filter((entity) => !!entity[field]);
  return filledEntities.length === emailAssociatedEntities.length
    ? true
    : filledEntities.length > 0
      ? "partial"
      : false;
};

export type EmailActivitiesCreateModalProps = {
  emailAssociatedEntities: EmailAssociatedEntity[];
  fixedCompany?: Company;
} & Pick<
  BaseEmailActivitiesCreateModalProps,
  "formLayout" | "onBack" | "onCreate" | "onFormLayoutChange" | "onHide"
>;

const EmailActivitiesCreateModal = ({
  associations,
  emailActivityType,
  emailAssociatedEntities,
  fixedCompany,
  formLayout,
  onAssociatedEntitiesChange,
  onBack,
  onCreate,
  onEntitiesRelateUnrelate,
  onFormLayoutChange,
  onHide,
  onInitialize,
}: EmailActivitiesCreateModalProps &
  ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps) => {
  const intl = useIntl();

  const { entities, entityType } = useNonNullableContext(EmailContext);

  const isBulkEmail = entities.length > 1;

  const [schemaFields] = useSchema({
    crmActivityTypeId: emailActivityType?.id,
    entityType: EntityType.ACTIVITY,
    isCreateForm: true,
    layout: formLayout,
  });

  const relationshipFieldsAutoFillState: Record<
    EntityTypesSupportingEmail,
    RelationshipFieldAutoFillState
  > = useMemo(() => {
    return {
      [EntityType.COMPANY]: getRelationshipFieldAutoFillState(emailAssociatedEntities, "accountId"),
      [EntityType.DEAL]: getRelationshipFieldAutoFillState(emailAssociatedEntities, "dealId"),
      [EntityType.PERSON]: getRelationshipFieldAutoFillState(emailAssociatedEntities, "contactId"),
    };
  }, [emailAssociatedEntities]);

  const companyFieldLocked =
    isBulkEmail ||
    entityType === EntityType.COMPANY ||
    !!fixedCompany ||
    relationshipFieldsAutoFillState[EntityType.COMPANY] !== false;
  const personFieldLocked =
    isBulkEmail ||
    entityType === EntityType.PERSON ||
    relationshipFieldsAutoFillState[EntityType.PERSON] !== false;
  const dealFieldLocked = isBulkEmail || relationshipFieldsAutoFillState[EntityType.DEAL] !== false;

  const relationshipFieldsAutoFilledByDealRelationship = useMemo(() => {
    return emailAssociatedEntities.some((emailAssociatedEntity) => !!emailAssociatedEntity.dealId);
  }, [emailAssociatedEntities]);

  const hasMissedAutoFilledRequiredRelationshipField = useMemo(() => {
    return (
      isBulkEmail &&
      schemaFields.some((schemaField) => {
        if (!schemaField.required) {
          return false;
        }

        const field = activityFieldModel.getByPlatformName(schemaField.field);
        if (!field || !field.hasFeature(FieldFeature.ASSOCIATED_ENTITY)) {
          return false;
        }

        let relationshipEntityType: EntityTypesSupportingEmail | undefined;
        switch (field.name) {
          case ActivityFieldName.COMPANY: {
            relationshipEntityType = EntityType.COMPANY;
            break;
          }
          case ActivityFieldName.PEOPLE:
          case ActivityFieldName.PERSON: {
            relationshipEntityType = EntityType.PERSON;
            break;
          }
          case ActivityFieldName.DEAL: {
            relationshipEntityType = EntityType.DEAL;
            break;
          }
        }

        return (
          relationshipEntityType &&
          relationshipEntityType !== entityType &&
          relationshipFieldsAutoFillState[relationshipEntityType] !== true
        );
      })
    );
  }, [schemaFields, isBulkEmail, entityType, relationshipFieldsAutoFillState]);

  const sendAndCreateDisabled = hasMissedAutoFilledRequiredRelationshipField ? true : undefined;

  const sendAndCreateTooltipTitle = hasMissedAutoFilledRequiredRelationshipField
    ? intl.formatMessage(messages.autoFilledRequiredRelationshipFieldMissedTooltip)
    : undefined;

  const noAssociations = useMemo(
    () =>
      !associations.associatedCompany &&
      !associations.associatedDeal &&
      !associations.associatedPerson,
    [associations.associatedCompany, associations.associatedDeal, associations.associatedPerson]
  );

  const associatedPeople = useMemo(
    () => (associations.associatedPerson ? [associations.associatedPerson] : undefined),
    [associations.associatedPerson]
  );

  useEffect(() => {
    onInitialize({ fixedCompany });
  }, [fixedCompany, onInitialize]);

  const handleCreate = useDynamicCallback<BaseEmailActivitiesCreateModalProps["onCreate"]>(
    ({ activity, fieldsValueValidateCallback, schedule }) => {
      const singleRecipientAssociations = !isBulkEmail ? emailAssociatedEntities[0] : undefined;
      onCreate({
        activity: {
          ...activity,
          ...(fixedCompany ? { companyId: fixedCompany.id } : {}),
          ...(singleRecipientAssociations?.dealId
            ? { deal: { id: singleRecipientAssociations.dealId } }
            : {}),
          ...(singleRecipientAssociations?.accountId
            ? { account: { id: singleRecipientAssociations.accountId } }
            : {}),
          ...(singleRecipientAssociations?.contactId
            ? { contact: { id: singleRecipientAssociations.contactId } }
            : {}),
        },
        fieldsValueValidateCallback,
        schedule,
      });
    }
  );

  const renderCompanyField: BaseEmailActivitiesCreateModalProps["renderCompanyField"] = ({
    disabled,
    label,
    required,
  }) =>
    companyFieldLocked ? (
      <Tooltip
        title={
          isBulkEmail
            ? intl.formatMessage(messages.autoFillingRelationshipFieldDisabledTooltip)
            : undefined
        }
      >
        <LockedEntityType
          caption={
            fixedCompany
              ? undefined
              : relationshipFieldsAutoFillState[EntityType.COMPANY] === "partial"
                ? relationshipFieldsAutoFilledByDealRelationship
                  ? intl.formatMessage(
                      messages.autoFilledWithDealRelationshipIfOnlyOneRelationshipExists
                    )
                  : intl.formatMessage(
                      messages.autoFilledWithRecipientRelationshipIfOnlyOneRelationshipExists
                    )
                : undefined
          }
          entityType={EntityType.COMPANY}
          label={label}
          placeholder={
            fixedCompany?.name ??
            (entityType === EntityType.COMPANY
              ? intl.formatMessage(messages.autoFilledWithRecipient)
              : relationshipFieldsAutoFillState[EntityType.COMPANY] !== false
                ? relationshipFieldsAutoFilledByDealRelationship
                  ? intl.formatMessage(messages.autoFilledWithDealRelationship)
                  : intl.formatMessage(messages.autoFilledWithRecipientRelationship)
                : undefined)
          }
          required={required}
        />
      </Tooltip>
    ) : (
      <CompanyRelationshipField
        allowAdd
        associatedCompany={associations.associatedCompany}
        associatedDeal={associations.associatedDeal}
        associatedPerson={associations.associatedPerson}
        disabled={disabled}
        entityType={EntityType.ACTIVITY}
        label={label}
        onChangeAssociatedEntities={onAssociatedEntitiesChange}
        relateEntities={onEntitiesRelateUnrelate}
        required={required}
        suggestedCompanies={noAssociations ? undefined : associations.availableCompanies}
      />
    );

  const renderDealField: BaseEmailActivitiesCreateModalProps["renderDealField"] = ({
    disabled,
    label,
    required,
  }) =>
    dealFieldLocked ? (
      <Tooltip
        title={
          isBulkEmail
            ? intl.formatMessage(messages.autoFillingRelationshipFieldDisabledTooltip)
            : undefined
        }
      >
        <LockedEntityType
          entityType={EntityType.DEAL}
          label={label}
          placeholder={
            relationshipFieldsAutoFillState[EntityType.DEAL] !== false
              ? intl.formatMessage(messages.autoFilledWithSelectedDeals)
              : undefined
          }
          required={required}
        />
      </Tooltip>
    ) : (
      <DealRelationshipField
        allowAdd
        associatedCompany={associations.associatedCompany}
        associatedDeal={associations.associatedDeal}
        associatedPerson={associations.associatedPerson}
        disabled={disabled}
        field={activityFieldModel.getByName(ActivityFieldName.DEAL)!}
        onChangeAssociatedEntities={onAssociatedEntitiesChange}
        relateEntities={onEntitiesRelateUnrelate}
        required={required}
        suggestedDeals={noAssociations ? undefined : associations.availableDeals}
      />
    );

  const renderPersonField: BaseEmailActivitiesCreateModalProps["renderPersonField"] = ({
    disabled,
    label,
    required,
  }) =>
    personFieldLocked ? (
      <Tooltip
        title={
          isBulkEmail
            ? intl.formatMessage(messages.autoFillingRelationshipFieldDisabledTooltip)
            : undefined
        }
      >
        <LockedEntityType
          caption={
            relationshipFieldsAutoFillState[EntityType.PERSON] === "partial"
              ? relationshipFieldsAutoFilledByDealRelationship
                ? intl.formatMessage(
                    messages.autoFilledWithDealRelationshipIfOnlyOneRelationshipExists
                  )
                : intl.formatMessage(
                    messages.autoFilledWithRecipientRelationshipIfOnlyOneRelationshipExists
                  )
              : undefined
          }
          entityType={EntityType.PERSON}
          label={label}
          placeholder={
            entityType === EntityType.PERSON
              ? intl.formatMessage(messages.autoFilledWithRecipient)
              : relationshipFieldsAutoFillState[EntityType.PERSON] !== false
                ? relationshipFieldsAutoFilledByDealRelationship
                  ? intl.formatMessage(messages.autoFilledWithDealRelationship)
                  : intl.formatMessage(messages.autoFilledWithRecipientRelationship)
                : undefined
          }
          required={required}
        />
      </Tooltip>
    ) : (
      <PersonRelationshipField
        allowAdd
        associatedCompany={associations.associatedCompany}
        associatedDeal={associations.associatedDeal}
        associatedPeople={associatedPeople}
        disabled={disabled}
        label={label}
        onChangeAssociatedEntities={onAssociatedEntitiesChange}
        relateEntities={onEntitiesRelateUnrelate}
        required={required}
        suggestedPeople={noAssociations ? undefined : associations.availablePeople}
      />
    );

  return (
    <BaseEmailActivitiesCreateModal
      formLayout={formLayout}
      onBack={onBack}
      onCreate={handleCreate}
      onFormLayoutChange={onFormLayoutChange}
      onHide={onHide}
      recipientCount={entities.length}
      recipientType={entityType}
      renderCompanyField={renderCompanyField}
      renderDealField={renderDealField}
      renderPersonField={renderPersonField}
      schemaFields={schemaFields}
      sendAndCreateDisabled={sendAndCreateDisabled}
      sendAndCreateTooltipTitle={sendAndCreateTooltipTitle}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  associations: getAssociationsState(state),
  emailActivityType: getEmailActivityType(state),
});

const mapDispatchToProps = {
  onAssociatedEntitiesChange: changeAssociatedEntities.request,
  onEntitiesRelateUnrelate: relateUnrelateEntities.request,
  onInitialize: initializeActivityModal.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailActivitiesCreateModal);
